// src/pos-app/shell/BottomTabs.js
//
// Shared station bottom tab bar (F12a Task 6.2) — glass chrome navigation
// matching the approved mockups (docs/mockups/f12a/*.html).
//
//   <BottomTabs
//     tabs={[{ key, label, icon, disabled?, badge? }]}   ≤5 tabs; label pre-translated
//     activeKey="sell"
//     onChange={(key) => ...}                    not fired for disabled tabs
//   />
//
// `icon` is either an inline SVG element or the name of a built-in icon
// ('grid' | 'receipt' | 'clock' | 'table'). Safe-area aware (--safe-bottom),
// ≥44px touch targets, RTL-safe (flex only, no directional offsets).
//
// `shortLabel` (F15 part 2, additive, optional per tab): the label to use at
// phone width. Long labels — "Clock In / Out" is the worst — truncate below
// ~400px because five tabs share the row. The choice lives HERE rather than in
// each station so callers cannot drift on the breakpoint; a tab without a
// `shortLabel` renders `label` at every width exactly as before.
//
// `badge` (Task N — user-QC finding 3, additive, optional number per tab):
// a small red count bubble in the tab's top-inline-end corner, per the
// original mockup's bottom-tab Orders badge (docs/mockups/f12a/
// cashier-station.html). Omitted (undefined/0/falsy) → no bubble, zero
// markup/CSS change from before — existing callers (Waiter/KDS, and Cashier
// legacy mode) that never pass `badge` render byte-identical to before.
import React from 'react';
// No glass() import: this bar uses the shared .aq-liquid class instead (see
// the note above the return), which AuroraBackground injects app-wide.
import useIsMobile from '../../hooks/useIsMobile';

const MAX_TABS = 5;

const CSS = `
/* Anchored, full-bleed tab bar — the native shape.
   It used to be an inset floating card (the station frame's 14px side padding
   applied to it and there was a gap beneath), which reads as a widget sitting
   on a page rather than as system chrome. A tab bar should span the full width
   and meet the bottom edge of the display, with content passing UNDER it. The
   station frames now render this OUTSIDE their padded content column so it can
   reach the edges; .btb-bar therefore owns its own horizontal padding.

   No --safe-bottom here on iOS: capacitor.config.ts sets ios.contentInset
   'always', so the WebView is already inset above the home indicator and the
   token resolves to 0 (see platform/nativeChrome.js). The calc() is kept so
   web and Electron, where env() IS the only mechanism, still clear it. */
.btb-bar{display:flex;justify-content:center;gap:6px;flex:none;box-sizing:border-box;
  width:100%;padding:8px 10px calc(8px + var(--safe-bottom));
  border-top:1px solid color-mix(in srgb, var(--c-border) 45%, transparent);
  box-shadow:0 -8px 26px color-mix(in srgb, var(--c-ink) 7%, transparent);}
.btb-tab{flex:1;max-width:220px;min-height:52px;min-width:44px;position:relative;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;
  border:none;border-radius:15px;background:transparent;cursor:pointer;
  color:var(--c-muted);font-family:inherit;font-size:11.5px;font-weight:800;letter-spacing:.2px;
  -webkit-tap-highlight-color:transparent;touch-action:manipulation;
  transition:color .22s ease, transform .16s ease;}
.btb-tab:active{transform:scale(.94);}

/* Active "lens" — a capsule of brighter glass behind the active tab rather
   than a flat fill, so the selection reads as part of the same material.
   Animated on opacity/transform only (never width/height) to stay off the
   layout path. */
.btb-tab::before{content:"";position:absolute;inset:0;border-radius:15px;z-index:-1;
  background:color-mix(in srgb, var(--c-green) 13%, transparent);
  border:1px solid color-mix(in srgb, var(--c-green) 22%, transparent);
  box-shadow:inset 0 1px 0 color-mix(in srgb, var(--c-white) 45%, transparent);
  opacity:0;transform:scale(.82);
  transition:opacity .22s ease, transform .28s cubic-bezier(.32,.72,0,1);}
.btb-tab.btb-on{color:var(--c-green);}
.btb-tab.btb-on::before{opacity:1;transform:scale(1);}
@media (prefers-reduced-motion: reduce){
  .btb-tab,.btb-tab::before{transition:none;}
}

.btb-tab:disabled{opacity:.38;cursor:default;}
.btb-tab:disabled:active{transform:none;}
.btb-ic{display:flex;align-items:center;justify-content:center;}
.btb-label{line-height:1;white-space:nowrap;}
/* Below 400px five tabs no longer fit at 11.5px without clipping the widest
   label. Step the type down rather than let it truncate — the icon carries
   recognition, but a half-word ("Clock In / Ou…") reads as broken. */
@media (max-width:400px){
  .btb-bar{gap:3px;padding-inline:5px;}
  .btb-tab{font-size:10px;letter-spacing:0;}
}
.btb-badge{position:absolute;top:2px;inset-inline-end:14px;min-width:17px;height:17px;padding:0 4px;
  border-radius:999px;background:var(--c-red);color:#fff;font-size:10px;font-weight:800;
  display:flex;align-items:center;justify-content:center;border:2px solid var(--c-surface);
  font-variant-numeric:tabular-nums;}
`;

// Small built-in icon set for the common station tabs.
export const TAB_ICONS = {
    grid: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="7.5" height="7.5" rx="1.8" />
            <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.8" />
            <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.8" />
            <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.8" />
        </svg>
    ),
    receipt: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 3h16v18l-2.5-1.5L15 21l-2.5-1.5L10 21l-2.5-1.5L5 21l-1-.6V3z" />
            <path d="M8 8h8M8 12h6" />
        </svg>
    ),
    clock: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </svg>
    ),
    table: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <ellipse cx="12" cy="6.5" rx="9" ry="3" />
            <path d="M12 9.5V21M5 8.6 4 20M19 8.6 20 20" />
        </svg>
    ),
};

export default function BottomTabs({ tabs = [], activeKey, onChange }) {
    const shown = tabs.slice(0, MAX_TABS);
    const isMobile = useIsMobile();

    // `aq-liquid` is the shared Liquid Glass material (AuroraBackground.js).
    // It replaces the old inline glass() card style: this bar is chrome that
    // content passes UNDER, not a panel sitting beside content, so it wants
    // the heavier blur, the specular rim and an upward shadow rather than a
    // card's border and drop shadow.
    return (
        <nav className="btb-bar aq-liquid" role="tablist">
            <style>{CSS}</style>
            {shown.map((tab) => {
                const active = tab.key === activeKey;
                const icon = typeof tab.icon === 'string' ? TAB_ICONS[tab.icon] || null : tab.icon;

                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={!!tab.disabled}
                        className={`btb-tab${active ? ' btb-on' : ''}`}
                        onClick={() => {
                            if (tab.disabled) return;
                            if (onChange) onChange(tab.key);
                        }}
                    >
                        {icon ? <span className="btb-ic" aria-hidden="true">{icon}</span> : null}
                        <span className="btb-label">
                            {isMobile && tab.shortLabel ? tab.shortLabel : tab.label}
                        </span>
                        {tab.badge > 0 ? (
                            <span className="btb-badge" data-testid={`tab-badge-${tab.key}`}>{tab.badge}</span>
                        ) : null}
                    </button>
                );
            })}
        </nav>
    );
}
